import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as statisticsService from '../services/withdrawal-statistics.service';
import type {
  WithdrawalStatisticsRange,
  WithdrawalStatisticsResponseDTO,
} from '../types/withdrawal-statistics.types';

const VALID_RANGES = new Set<WithdrawalStatisticsRange>([
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'custom',
]);

function todayString(): string {
  const today = new Date();
  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');
}

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      'admin role required': '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid withdrawal statistics date range': '日期范围无效',
      'invalid withdrawal statistics channel': '出款通道无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useWithdrawalStatistics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawRange = searchParams.get('range') || 'today';
  const range: WithdrawalStatisticsRange = VALID_RANGES.has(rawRange as WithdrawalStatisticsRange)
    ? (rawRange as WithdrawalStatisticsRange)
    : 'today';
  const startDate = searchParams.get('startDate') || todayString();
  const endDate = searchParams.get('endDate') || todayString();
  const channel = searchParams.get('channel') || '';

  const [data, setData] = useState<WithdrawalStatisticsResponseDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef(0);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setLoading(true);
    setError(null);
    try {
      const result = await statisticsService.getWithdrawalStatistics({
        range,
        startDate,
        endDate,
        channel,
      });
      if (requestSeqRef.current !== requestSeq) return;
      setData(result);
    } catch (e) {
      if (requestSeqRef.current !== requestSeq) return;
      setError(resolveError(e));
    } finally {
      if (requestSeqRef.current !== requestSeq) return;
      setLoading(false);
    }
  }, [channel, endDate, range, startDate]);

  useEffect(() => {
    load();
  }, [load]);

  const setRange = useCallback((value: WithdrawalStatisticsRange) => {
    updateParams({ range: value === 'today' ? null : value });
  }, [updateParams]);

  const setCustomDateRange = useCallback((nextStartDate: string, nextEndDate: string) => {
    updateParams({ range: 'custom', startDate: nextStartDate, endDate: nextEndDate });
  }, [updateParams]);

  const setChannel = useCallback((value: string) => {
    updateParams({ channel: value || null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return useMemo(() => ({
    data,
    loading,
    error,
    range,
    startDate,
    endDate,
    channel,
    setRange,
    setCustomDateRange,
    setChannel,
    resetFilters,
    reload: load,
  }), [
    channel,
    data,
    endDate,
    error,
    load,
    loading,
    range,
    resetFilters,
    setChannel,
    setCustomDateRange,
    setRange,
    startDate,
  ]);
}
